import React from 'react';
import styled from 'styled-components';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn, PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import type { ExhibitionBulkSheetParseResult } from './parseExhibitionBulkSheet';

interface ExhibitionBulkPreviewProps {
  fileName: string;
  result: ExhibitionBulkSheetParseResult;
  onCancel: () => void;
  onConfirm: () => void;
}

const ExhibitionBulkPreview: React.FC<ExhibitionBulkPreviewProps> = ({
  fileName,
  result,
  onCancel,
  onConfirm,
}) => {
  const rows = [
    ...result.exhibitionList.map(({ rowNumber, exhibition }) => ({
      rowNumber,
      title: exhibition.title,
      ready: true,
      message: `${exhibition.venueCode} · ${exhibition.artists.length} artist(s)`,
    })),
    ...result.invalidResults.map((item) => ({
      rowNumber: item.rowNumber,
      title: '',
      ready: false,
      message: item.message ?? 'Invalid row',
    })),
  ].sort((left, right) => left.rowNumber - right.rowNumber);

  return (
    <PreviewContainer aria-label="Exhibition bulk import preview">
      <Title>Import preview</Title>
      <FileName title={fileName}>{fileName}</FileName>
      <Summary>
        {result.totalCount} rows · {result.exhibitionList.length} ready · {result.invalidResults.length} error
      </Summary>
      {result.invalidResults.length > 0 && (
        <Notice>Error rows will be skipped.</Notice>
      )}
      <PreviewList>
        {rows.map((item) => (
          <PreviewItem key={item.rowNumber} $ready={item.ready}>
            <RowMeta>
              Row {item.rowNumber}{item.title ? ` · ${item.title}` : ''}
            </RowMeta>
            <Status $ready={item.ready}>{item.ready ? 'Ready' : 'Error'}</Status>
            <Message>{item.message}</Message>
          </PreviewItem>
        ))}
      </PreviewList>
      <ButtonContainer>
        <HeadlessBtn
          value="Create"
          handleClick={onConfirm}
          disabled={result.exhibitionList.length === 0}
          StyledBtn={BlackFitBtn}
        />
        <HeadlessBtn value="Cancel" handleClick={onCancel} StyledBtn={PaperBlurFitBtn} />
      </ButtonContainer>
    </PreviewContainer>
  );
};

const PreviewContainer = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 8px 0 40px;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const FileName = styled.div`
  overflow: hidden;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const Summary = styled.div`
  padding: 12px 0 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Notice = styled.div`
  padding-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const PreviewList = styled.div`
  min-height: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  &::-webkit-scrollbar { display: none; }
`;

const PreviewItem = styled.article<{ $ready: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  opacity: ${({ $ready }) => ($ready ? 1 : 0.8)};
`;

const RowMeta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Status = styled.div<{ $ready: boolean }>`
  color: ${({ theme, $ready }) =>
    $ready ? theme.colors.color_Gray_05 : theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_04};
`;

const Message = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  word-break: break-word;
  ${({ theme }) => theme.typography.Body_04};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
`;

export default ExhibitionBulkPreview;
